import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from './context/AuthContext';
import { PartnerAuthProvider, usePartnerAuth } from './context/PartnerAuthContext';
import { Header, Footer } from './components/Layout';
import WhatsAppButton from './components/WhatsAppButton';
import JsonLd from './components/JsonLd';
import SiteVerification from './components/SiteVerification';
import { ConsentProvider } from './seo/consent/ConsentContext';
import ConsentBanner from './seo/consent/ConsentBanner';
import RouteGuard, { AdminRoute } from './components/RouteGuard';
import { isAdminDomain, isDevelopment } from './utils/domainAccess';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import ServicePage from './pages/ServicePage';
import ContentRoute from './components/content/ContentRoute';
import SocialLinks from './components/SocialLinks';
import EntreprisesPage from './pages/EntreprisesPage';
import ReserverPage from './pages/ReserverPage';
import ContactPage from './pages/ContactPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import ConfidentialitePage from './pages/ConfidentialitePage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminV3 from './pages/AdminV3';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import DriverLoginPage from './pages/DriverLoginPage';
import DriverDashboard from './pages/DriverDashboard';
import CorporateLoginPage from './pages/CorporateLoginPage';
import CorporateDashboard from './pages/CorporateDashboard';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import DevisValidatePage from './pages/DevisValidatePage';
import TrackingPage from './pages/TrackingPage';

// Partner Pages
import { PartnerLayout } from './components/partner/PartnerLayout';
import {
  PartnerLogin,
  PartnerDashboard,
  PartnerBookings,
  PartnerBookingNew,
  PartnerBookingDetail,
  PartnerInvoices,
  PartnerInvoiceDetail,
  PartnerPayments
} from './pages/partner';

// Legal Pages
import CGUPage from './pages/legal/CGUPage';
import MentionsLegalesRGPDPage from './pages/legal/MentionsLegalesRGPDPage';
import RefundPolicyPage from './pages/legal/RefundPolicyPage';
import ChauffeurReglementPage from './pages/legal/ChauffeurReglementPage';
import ChauffeurContractPage from './pages/legal/ChauffeurContractPage';

// B3-12: /admin/* is the canonical admin namespace; the historical /admin-v3/*
// URLs are kept as a compatibility layer that redirects to the equivalent
// /admin/* route, preserving the path suffix, query string and hash. `replace`
// keeps these legacy URLs out of history so Back never bounces between the two
// namespaces. The redirect targets the AdminRoute-guarded canonical app, so it
// never exposes a page ahead of the auth guard.
export const AdminV3CompatRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const suffix = pathname.replace(/^\/admin-v3/, ''); // '' for bare /admin-v3
  return <Navigate to={`/admin${suffix}${search}${hash}`} replace />;
};

// Partner Route Guard
const PartnerRoute = ({ children }) => {
  const { isAuthenticated, loading } = usePartnerAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/partner/login" replace />;
  }
  
  return <PartnerLayout>{children}</PartnerLayout>;
};

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isDriverRoute = location.pathname.startsWith('/driver');
  const isCorporateRoute = location.pathname.startsWith('/company');
  const isPartnerRoute = location.pathname.startsWith('/partner');
  const isTrackingRoute = location.pathname.startsWith('/tracking');
  
  // Determine if we should show admin-only UI
  const showAdminUI = isAdminRoute && (isAdminDomain() || isDevelopment());

  return (
    <RouteGuard>
      <div className="min-h-screen bg-black">
        <div className="noise-overlay" />
        {/* C1B — route-driven JSON-LD (emits only on indexable public routes; null elsewhere) */}
        <JsonLd />
        {/* WP-3.1 — sitewide search-engine verification metas (dark unless tokens are set) */}
        <SiteVerification />
        {!isAdminRoute && !isDriverRoute && !isCorporateRoute && !isPartnerRoute && !isTrackingRoute && <Header />}
        
        
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/entreprises" element={<EntreprisesPage />} />
          <Route path="/reserver" element={<ReserverPage />} />
          <Route path="/contact" element={<ContactPage />} />
          {/* About — first content page via the generic Content Platform host (no bespoke component).
              DPA-4: official social identities appended below the content (same links as footer/contact). */}
          <Route
            path="/about"
            element={
              <>
                <ContentRoute seoKey="about" />
                <section className="bg-[#f8f8f8] pb-16" data-testid="about-social">
                  <div className="max-w-[900px] mx-auto px-6 md:px-12">
                    <h2 className="font-heading text-xl font-bold text-black mb-4">Suivez Black Matte</h2>
                    <div className="flex flex-wrap gap-3">
                      <SocialLinks tone="light" />
                    </div>
                  </div>
                </section>
              </>
            }
          />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/confidentialite" element={<ConfidentialitePage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/devis/validate/:token" element={<DevisValidatePage />} />
          
          {/* Admin Routes - Protected by domain in production.
              B3-12: /admin/* is the CANONICAL admin namespace (Admin V3 is the sole
              admin interface). The historical /admin-v3/* URLs are kept as a
              compatibility redirect layer (below). Legacy V1/V2 URLs redirect
              straight to the canonical /admin/* target — no /admin-v3 intermediary. */}
          <Route path="/admin/login" element={
            <AdminRoute>
              <AdminLoginPage />
            </AdminRoute>
          } />
          {/* Legacy V1/V2 compatibility redirects → canonical /admin/* (single hop) */}
          <Route path="/admin/control-tower" element={<Navigate to="/admin/operations" replace />} />
          <Route path="/admin-v2" element={<Navigate to="/admin/overview" replace />} />
          {/* B3-12 compatibility layer: every historical /admin-v3/<path> redirects
              to the canonical /admin/<path> (suffix + query + hash preserved). */}
          <Route path="/admin-v3/*" element={<AdminV3CompatRedirect />} />
          {/* CANONICAL admin application. AdminV3's relative routes resolve under
              /admin/* (overview, bookings, bookings/:id, incidents, drivers, …);
              bare /admin lands on /admin/overview via the app's index redirect. */}
          <Route path="/admin/*" element={
            <AdminRoute>
              <AdminV3 />
            </AdminRoute>
          } />

          {/* Driver Routes */}
          <Route path="/driver/login" element={<DriverLoginPage />} />
          <Route path="/driver" element={<DriverDashboard />} />
          
          {/* Corporate Routes */}
          <Route path="/company/login" element={<CorporateLoginPage />} />
          <Route path="/company/dashboard" element={<CorporateDashboard />} />
          <Route path="/corporate/accept-invitation" element={<AcceptInvitationPage />} />
          
          {/* Tracking */}
          <Route path="/tracking/:bookingId" element={<TrackingPage />} />
          
          {/* Partner Routes */}
          <Route path="/partner/login" element={<PartnerLogin />} />
          <Route path="/partner/dashboard" element={
            <PartnerRoute>
              <PartnerDashboard />
            </PartnerRoute>
          } />
          <Route path="/partner/bookings" element={
            <PartnerRoute>
              <PartnerBookings />
            </PartnerRoute>
          } />
          <Route path="/partner/bookings/new" element={
            <PartnerRoute>
              <PartnerBookingNew />
            </PartnerRoute>
          } />
          <Route path="/partner/bookings/:bookingId" element={
            <PartnerRoute>
              <PartnerBookingDetail />
            </PartnerRoute>
          } />
          <Route path="/partner/invoices" element={
            <PartnerRoute>
              <PartnerInvoices />
            </PartnerRoute>
          } />
          <Route path="/partner/invoices/:invoiceId" element={
            <PartnerRoute>
              <PartnerInvoiceDetail />
            </PartnerRoute>
          } />
          <Route path="/partner/payments" element={
            <PartnerRoute>
              <PartnerPayments />
            </PartnerRoute>
          } />
          
          {/* Legal Pages */}
          <Route path="/legal/cgu" element={<CGUPage />} />
          <Route path="/legal/mentions-legales" element={<MentionsLegalesRGPDPage />} />
          <Route path="/legal/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/legal/chauffeur-reglement" element={<ChauffeurReglementPage />} />
          <Route path="/legal/chauffeur-contract" element={<ChauffeurContractPage />} />
        </Routes>

        {!isAdminRoute && !isDriverRoute && !isCorporateRoute && !isPartnerRoute && !isTrackingRoute && <Footer />}
        {!isAdminRoute && !isDriverRoute && !isCorporateRoute && !isPartnerRoute && !isTrackingRoute && <WhatsAppButton />}
        {/* WP-3.1 — RGPD consent banner (public surface only; dark unless VITE_CONSENT_ENABLED) */}
        {!isAdminRoute && !isDriverRoute && !isCorporateRoute && !isPartnerRoute && !isTrackingRoute && <ConsentBanner />}
      </div>
    </RouteGuard>
  );
};

const AdminHeader = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-[1600px] mx-auto px-6">
        <div className="flex items-center h-16">
          <a href="/admin" className="font-heading text-xl font-bold text-white tracking-tight">
            BLACK MATTE
          </a>
          <span className="ml-4 text-xs uppercase tracking-[0.2em] text-neutral-500">Administration</span>
        </div>
      </div>
    </header>
  );
};

function App() {
  return (
    <AuthProvider>
      <PartnerAuthProvider>
        <BrowserRouter>
          <ConsentProvider>
            <AppContent />
          </ConsentProvider>
        </BrowserRouter>
      </PartnerAuthProvider>
    </AuthProvider>
  );
}

export default App;
